import { useState, useImperativeHandle, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { userService, type User } from '@/services/userService'

interface DeleteUserModalProps {
  onSuccess?: () => void
}

export interface DeleteUserModalRef {
  open: (user: User) => void
  close: () => void
}

const DeleteUserModal = forwardRef<DeleteUserModalRef, DeleteUserModalProps>(({ onSuccess }, ref) => {
  const { t } = useTranslation('users')
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [user, setUser] = useState<User | null>(null)
  const [confirmText, setConfirmText] = useState('')

  useImperativeHandle(ref, () => ({
    open: (userData: User) => {
      setUser(userData)
      setIsOpen(true)
      setError('')
      setSuccess(false)
      setConfirmText('')
    },
    close: () => {
      setIsOpen(false)
      setUser(null)
      setConfirmText('')
      setSuccess(false)
    },
  }))

  const expectedConfirmText = user ? `delete ${user.name}` : ''
  const isConfirmValid = confirmText === expectedConfirmText

  const handleDelete = async () => {
    if (!user || !isConfirmValid) return

    try {
      setLoading(true)
      setError('')

      await userService.deleteUser(user.id)

      // Show success message instead of closing immediately
      setSuccess(true)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'errors.operationFailed'
      if (errorMessage.startsWith('errors.')) {
        setError(t(errorMessage, errorMessage))
      } else {
        setError(errorMessage)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    const wasSuccess = success
    setIsOpen(false)
    setUser(null)
    setConfirmText('')
    setError('')
    setSuccess(false)

    // Only call onSuccess if the operation was successful
    if (wasSuccess) {
      onSuccess?.()
    }
  }

  if (!isOpen || !user) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/30 backdrop-blur-md">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-red-600">{t('deleteModal.title', 'Delete User')}</h2>
              <p className="text-sm text-gray-600 mt-1">{t('deleteModal.subtitle', 'This action cannot be undone')}</p>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={loading}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Success Message */}
          {success && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-green-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-green-900 mb-1">
                    {t('deleteModal.successTitle', 'User deleted successfully!')}
                  </p>
                  <p className="text-xs text-green-700">
                    {t('deleteModal.successDesc', 'The user has been permanently removed from the system.')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && !success && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <svg className="w-5 h-5 text-red-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* User Info */}
          {!success && (
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-3">{t('deleteModal.message', 'Are you sure you want to delete the following user?')}</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">{t('table.account', 'Account')}:</span>
                <span className="text-sm text-gray-900">{user.account}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">{t('table.name', 'Name')}:</span>
                <span className="text-sm text-gray-900">{user.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">{t('table.role', 'Role')}:</span>
                <span className="text-sm text-gray-900">{t(`roles.${user.platform_role}`, user.platform_role)}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">{t('table.type', 'Type')}:</span>
                <span className="text-sm text-gray-900">{t(`types.${user.type}`, user.type)}</span>
              </div>
            </div>
          </div>
          )}

          {/* Warning Box */}
          {!success && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-yellow-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-yellow-800 mb-2">
                  {t('deleteModal.warning', 'Warning')}
                </h4>
                <ul className="text-sm text-yellow-700 space-y-1 list-disc list-inside">
                  <li>{t('deleteModal.warningItem1', 'All user data will be permanently deleted')}</li>
                  <li>{t('deleteModal.warningItem2', 'User permissions and settings will be removed')}</li>
                  {(user.type === 'ldap' || user.type === 'oidc') && (
                    <li>{t('deleteModal.warningItem3', 'External users will be automatically recreated when they log in again')}</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
          )}

          {/* Confirmation Input */}
          {!success && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('deleteModal.confirmText', 'Please type "delete {{userName}}" to confirm deletion', { userName: user.name })}
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={expectedConfirmText}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                confirmText && !isConfirmValid
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-primary'
              }`}
              disabled={loading}
            />
            {confirmText && !isConfirmValid && (
              <p className="text-sm text-red-600 mt-1">
                {t('deleteModal.confirmMismatch', 'The entered text does not match')}
              </p>
            )}
          </div>
          )}

          {/* Proceed Warning */}
          {!success && (
          <p className="text-sm text-gray-600 italic">
            {t('deleteModal.proceedWarning', 'Please proceed with caution')}
          </p>
          )}
        </div>

        {/* Actions */}
        <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
          {success ? (
            <button
              type="button"
              onClick={handleClose}
              className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              {t('deleteModal.close', 'Close')}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={loading}
              >
                {t('deleteModal.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                disabled={loading || !isConfirmValid}
              >
                {loading && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                )}
                <span>{loading ? t('deleteModal.deleting', 'Deleting') : t('deleteModal.delete', 'Delete User')}</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
})

DeleteUserModal.displayName = 'DeleteUserModal'

export default DeleteUserModal
